import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import VideoThumbnail from 'react-video-thumbnail'
import useFileUpload from 'hooks/useFileUpload'
import Stores from 'Stores'
import VariantDel from 'components/ContentBox/Global/GlobalModalSingle/templates/StudyTest/TestQuestion/components/VariantDel'
import st from './FileSelector.module.scss'
import { ReactComponent as Basket } from 'components/ContentBox/img/delete-mini-icon.svg'
import { ReactComponent as EditIcon } from 'components/UI/icons/constructor-edit/edit-img.svg'
import { ReactComponent as Eye } from 'components/ContentBox/img/eye-mini-icon.svg'
import UploadIcon from 'components/ContentBox/img/upload-cloud-icon.svg'
import { ReactComponent as PlusIcon } from 'components/UI/icons/constructor-edit/upload.svg'
import { ReactComponent as DocsIcon } from 'components/ContentBox/img/new_document_img.svg'
import FileSelectModal from './components/FileSelectModal'
import useLocalization from 'hooks/useLocalization'
import { SuperButton } from '../SuperButton/SuperButton'

const FileSelector = (props) => {
  const { iconType = 'delete', className = '' } = props

  const [error, setError] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [serverFilePath, setServerFilePath] = useState('')
  const [src, setSrc] = useState('')
  const [editMode, setEditMode] = useState(false)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const uploadRef = useRef(null)

  const path = props.crm ? Stores.baseLinkCRM() : Stores.baseLink()

  const uploadUrl = props.crm
    ? 'files/file_upload'
    : props.constructor
      ? 'files/image_upload'
      : 'ebooks/file_upload'

  const { refInput, progress, file, setFile, isUploaded } = useFileUpload({
    onUploadEnd(e) {
      setServerFilePath(`${path}${e}`)
      props.onChange(e)
      setEditMode(false)
    },
    url: uploadUrl,
    ...(props.crm && { crm: props.crm }),
  })

  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'modal_file_selector',
    dict: {
      click_or_drag: 'Click or drag file to this area to upload',
      upload_info: 'Support for a single upload',
      prohibit_info:
        'Strictly prohibit from uploading company data or other band files',
      supported_formats: 'Supported formats',
    },
  })

  const getType = (name) => {
    let arr = []
    if (name) {
      arr = name.split('.')
      return arr[arr.length - 1].toUpperCase()
    } else {
      return 'ERR'
    }
  }

  const checkType = (file, allowedTypes) =>
    allowedTypes.includes(getType(file.name).toLocaleLowerCase())

  const onDrop = (e) => {
    e.preventDefault()
    const droppedFiles = e.dataTransfer.files
    if (droppedFiles.length > 0 && checkType(droppedFiles[0], props.types)) {
      setError(false)
      setFile(droppedFiles[0])
    } else {
      setError(true)
    }
    setIsDraggingOver(false)
  }

  const onDragOver = (e) => {
    e.preventDefault()
    setIsDraggingOver(true)
  }

  const onDragLeave = (e) => {
    e.preventDefault()
    setIsDraggingOver(false)
  }

  const deleteHandler = () => {
    setFile(null)
    props.onChange(null)
  }

  const handleIconClick = (e) => {
    e.stopPropagation()
    if (iconType === 'delete') {
      deleteHandler()
    } else if (iconType === 'edit') {
      setEditMode(true)
    }
  }

  const handleClickOutside = (event) => {
    if (
      uploadRef.current &&
      !uploadRef.current.contains(event.target) &&
      !event.target.closest(`.${st.btns}`)
    ) {
      setEditMode(false)
    }
  }

  useEffect(() => {
    if (editMode) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [editMode])

  useEffect(() => {
    const src = file
      ? URL.createObjectURL(file)
      : `${props.crm ? Stores.baseLinkCRM() : Stores.baseLink()}${props.value}`
    setSrc(src)
  }, [file, props.value])

  useEffect(() => {
    if (props.onUploadStateChange) {
      props.onUploadStateChange(isUploaded)
    }
  }, [isUploaded])

  const renderPreview = () => {
    switch (props.type) {
      case 'image':
        return (
          <div className={st.container}>
            <div className={`${st.preview}`}>
              <div className={st.image}>
                <img src={src} alt='' />
              </div>

              <div className={st.iconBox}>
                <div className={st.viewIcon}>
                  <Eye onClick={() => setShowModal(true)} />
                </div>
                {iconType === 'delete' ? (
                  <div className={st.deleteIcon}>
                    <Basket onClick={handleIconClick} />
                  </div>
                ) : (
                  <div className={st.editIcon}>
                    <EditIcon onClick={handleIconClick} />
                  </div>
                )}
              </div>
            </div>

            {editMode && (
              <div className={st.footer}>
                <div className={st.upload}>
                  <input
                    type='file'
                    ref={refInput}
                    onChange={(e) => {
                      if (checkType(e.target.files[0], props.types)) {
                        setError(false)
                        setFile(e.target.files[0])
                      } else setError(true)
                    }}
                    accept={`${props.type}/*`}
                  />
                  <SuperButton
                    className={st.uploadBtn}
                    variant='confirm'
                    onClick={() => {
                      refInput.current.click()
                    }}
                  >
                    <PlusIcon />
                    Upload image
                  </SuperButton>
                </div>
                <div className={st.btns}>{props.children}</div>
              </div>
            )}
          </div>
        )

      case 'audio':
        return (
          <div className={st.previewAudio}>
            <audio controls src={src || serverFilePath}>
              Your browser does not support the <code>audio</code> element.
            </audio>
            <VariantDel onClick={deleteHandler} />
          </div>
        )

      case 'video':
        return (
          <div className={st.preview}>
            <div>
              <VideoThumbnail videoUrl={src || serverFilePath} />
            </div>
            <div className={st.iconBox}>
              <div className={st.deleteIcon}>
                <Basket onClick={deleteHandler} />
              </div>
            </div>
          </div>
        )

      case 'file':
        const displayFileName =
          file?.name || props.value?.split('/').pop() || 'Файл'
        const displayFileType = getType(displayFileName)

        return (
          <div className={st.previewFile}>
            <div className={st.file}>
              <DocsIcon />
              <span>{displayFileType}</span>
            </div>
            <div className={st.fileName}>{displayFileName}</div>
            <div className={st.iconBox}>
              <div className={st.viewIcon}>
                <a
                  href={src || serverFilePath}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <Eye />
                </a>
              </div>
              <div className={st.deleteIcon}>
                <Basket
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteHandler()
                  }}
                />
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div ref={uploadRef} className={`${className}`}>
      {(file || props.value) && !error && !props.error ? (
        <div className={`${st.boxImg}  ${editMode ? st.editMode : ''}`}>
          {file && progress < 100 && (
            <div className={st.progress}>
              <progress max='100' value={progress} />
              <div>{progress} %</div>
            </div>
          )}
          {renderPreview()}
          {showModal && (
            <FileSelectModal img={src} setOpen={() => setShowModal(false)} />
          )}
        </div>
      ) : (
        <div
          onClick={() => {
            refInput.current.click()
          }}
          className={`${st.box} 
                  ${error || props.error ? st.boxError : ''} ${
                    isDraggingOver ? st.dragOver : ''
                  }`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragEnter={onDragOver}
          onDragLeave={onDragLeave}
        >
          <input
            type='file'
            ref={refInput}
            onChange={(e) => {
              if (checkType(e.target.files[0], props.types)) {
                setError(false)
                setFile(e.target.files[0])
              } else setError(true)
            }}
            accept={`${props.type}/*`}
          />
          <img className={st.uploadIcon} src={UploadIcon} alt='' />
          <div className={st.tip}>
            <h4>{translate.click_or_drag}</h4>
            <p>
              {translate.upload_info}. {translate.prohibit_info}.
            </p>
            <b>{translate.supported_formats}: </b>
            <span>{props.types.join(', ')}</span>
          </div>
        </div>
      )}
    </div>
  )
}

FileSelector.propTypes = {
  iconType: PropTypes.oneOf(['delete', 'edit']),
  type: PropTypes.oneOf(['image', 'audio', 'video', 'file']).isRequired,
  value: PropTypes.string.isRequired,
  types: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  onUploadStateChange: PropTypes.func,
  children: PropTypes.node,
}

FileSelector.defaultProps = {
  iconType: 'delete',
  type: 'image',
  onEdit: () => {},
}

export default FileSelector
